import Api from './api';

// ---------------------------------------------------------------------------
// Item 7 — sessões de usuário (API 2DO, api/users/sessions). JSON da API todo em minúsculo: normalizar aqui.
// Só GlobalAdministrator lista/derruba sessões de outros; "mine" é de qualquer usuário.
// ---------------------------------------------------------------------------

function unwrap(result) {
  const body = result && result.data;
  if (!body || body.success === false) throw new Error((body && body.errors && body.errors[0]) || 'Falha ao consultar as sessões.');
  return body;
}

export const normalizeSession = (s) => ({
  sessionId: s.sessionid ?? '', userId: s.userid ?? null,
  createdAt: s.createdat ?? null, lastRefreshAt: s.lastrefreshat ?? null, refreshCount: Number(s.refreshcount ?? 0),
  absoluteExpiresAt: s.absoluteexpiresat ?? null, ip: s.ip ?? '', userAgent: s.useragent ?? '', loginKind: s.loginkind ?? '',
  deviceKind: s.devicekind === 'mobile' ? 'mobile' : 'desktop',
  revokedAt: s.revokedat ?? null, revokedBy: s.revokedby ?? null, revokeReason: s.revokereason ?? '',
  status: s.status ?? (s.active ? 'active' : 'revoked'), current: !!s.current, active: !!s.active,
});

// Portão de sessão do contratante: ligado (padrão) ou desligado com quando/quem/por quê.
export const normalizeGate = (g) => ({
  enabled: g ? (g.enabled ?? true) !== false : true,
  offAt: (g && g.offat) ?? null, offUntil: (g && g.offuntil) ?? null, offBy: (g && g.offby) ?? null, offReason: (g && g.offreason) ?? '',
});

// Prazos (tela de configuração e topo de Sessões ativas).
export const normalizePolicy = (p) => ({
  tokenMinutes: Number(p.tokenminutes ?? 0), tokenSource: p.tokensource ?? 'padrao', idleMinutes: Number(p.idleminutes ?? 0), idleSource: p.idlesource ?? 'padrao',
  maxHours: Number(p.maxhours ?? 0), maxSource: p.maxsource ?? 'padrao', sessionsNotBefore: p.sessionsnotbefore ?? null,
  storedTokenMinutes: p.storedtokenminutes ?? null, storedIdleMinutes: p.storedidleminutes ?? null, storedMaxHours: p.storedmaxhours ?? null,
  // Item 5: limiar de destaque de linhas devolvidas (telas de auditoria de uso). API antiga sem o campo = 5000.
  volumeAlertRows: Number(p.volumealertrows ?? DEFAULT_VOLUME_ALERT_ROWS) || DEFAULT_VOLUME_ALERT_ROWS,
  limits: p.limits ? {
    tokenMin: p.limits.tokenmin, tokenMax: p.limits.tokenmax, idleMin: p.limits.idlemin, idleMax: p.limits.idlemax, maxHoursMin: p.limits.maxhoursmin, maxHoursMax: p.limits.maxhoursmax,
    volumeMin: p.limits.volumemin ?? 100, volumeMax: p.limits.volumemax ?? 1000000, volumeDefault: p.limits.volumedefault ?? DEFAULT_VOLUME_ALERT_ROWS,
  } : {},
});

export const DEFAULT_VOLUME_ALERT_ROWS = 5000;

const UserSessionService = {
  policy: () => Api.get('/users/sessions/policy').then((r) => normalizePolicy(unwrap(r).data || {})),
  // volumeAlertRows (item 5) é opcional: undefined/null = a API mantém o gravado.
  savePolicy: (tokenMinutes, idleMinutes, maxHours, volumeAlertRows) => Api.put('/users/sessions/policy', { tokenMinutes, idleMinutes, maxHours, volumeAlertRows: volumeAlertRows ?? null }).then((r) => normalizePolicy(unwrap(r).data || {})),
  // Tela Sessões ativas (contratante).
  listTenant: (f) => Api.get(`/users/sessions?onlyActive=${f.onlyActive ? 'true' : 'false'}&userId=${f.userId || 0}&ip=${encodeURIComponent(f.ip || '')}&agent=${encodeURIComponent(f.agent || '')}&sort=${f.sort || ''}&page=${f.page || 1}&pageSize=${f.pageSize || 50}`)
    .then((r) => { const d = unwrap(r).data || {}; return { count: Number(d.count ?? 0), data: (d.data || []).map((x) => ({ ...normalizeSession(x.session || {}), userName: x.username ?? '', userEmail: x.useremail ?? '' })) }; }),
  summaryTenant: () => Api.get('/users/sessions/summary').then((r) => {
    const d = unwrap(r).data || {}; const s = d.summary || {}; const p = d.policy || {};
    return {
      serverTime: d.servertime ?? null,
      gate: normalizeGate(d.gate),
      summary: { active: Number(s.active ?? 0), users: Number(s.users ?? 0), renewalsLastHour: Number(s.renewalslasthour ?? 0), loginsLastHour: Number(s.loginslasthour ?? 0), tokenExpiringSoon: Number(s.tokenexpiringsoon ?? 0), idleExpiringSoon: Number(s.idleexpiringsoon ?? 0), stale: Number(s.stale ?? 0) },
      policy: { tokenMinutes: Number(p.tokenminutes ?? 0), tokenSource: p.tokensource ?? '', idleMinutes: Number(p.idleminutes ?? 0), idleSource: p.idlesource ?? '', maxHours: Number(p.maxhours ?? 0), maxSource: p.maxsource ?? '', sessionsNotBefore: p.sessionsnotbefore ?? null },
    };
  }),
  revokeAllTenant: (reason, includeMe) => Api.post('/users/sessions/revoke-all-tenant', { reason: reason || '', includeMe: !!includeMe }).then((r) => unwrap(r).data),
  // Botão do portão de sessão do contratante (item 7 fase 2): enabled=false exige motivo e duração (1, 4 ou 24 h; religa sozinho).
  setGate: (enabled, reason, hours) => Api.put('/users/sessions/gate', { enabled: !!enabled, reason: reason || '', hours: Number(hours) || 4 }).then((r) => normalizeGate(unwrap(r).data)),
  list: (userId, onlyActive = true, limit = 50) => Api.get(`/users/sessions/${userId}?onlyActive=${onlyActive ? 'true' : 'false'}&limit=${limit}`)
    .then((r) => (unwrap(r).data || []).map(normalizeSession)),
  mine: (onlyActive = true) => Api.get(`/users/sessions/mine?onlyActive=${onlyActive ? 'true' : 'false'}`)
    .then((r) => (unwrap(r).data || []).map(normalizeSession)),
  revokeMine: (sessionId) => Api.post(`/users/sessions/mine/${sessionId}/revoke`, {}).then((r) => unwrap(r).data),
  revoke: (sessionId, reason) => Api.post(`/users/sessions/${sessionId}/revoke`, { reason: reason || '' }).then((r) => unwrap(r).data),
  revokeAll: (userId, reason) => Api.post(`/users/sessions/revoke-all/${userId}`, { reason: reason || '' }).then((r) => unwrap(r).data),
};

export default UserSessionService;
